import { useEffect, useRef, useState } from "react";
import { Button, Pane } from "../ui/index.js";
import { captureUiError } from "../sentry.js";

type State =
  | "waiting"
  | "exporting"
  | "resuming"
  | "completed"
  | "cancelled"
  | "expired"
  | "failed";
interface Session {
  state: State;
  expiresAt: string;
  browserReady?: boolean;
}
const states: State[] = [
  "waiting",
  "exporting",
  "resuming",
  "completed",
  "cancelled",
  "expired",
  "failed",
];
class AccessUnavailable extends Error {}
async function readSession(
  url: string,
  method = "GET",
  signal?: AbortSignal,
): Promise<Session> {
  const response = await fetch(url, {
    method,
    credentials: "include",
    cache: "no-store",
    signal,
  });
  if ([401, 403, 404, 410].includes(response.status))
    throw new AccessUnavailable();
  if (!response.ok) throw new Error("Private sign-in request failed.");
  const value: unknown = await response.json();
  if (
    !value ||
    typeof value !== "object" ||
    !("state" in value) ||
    !states.includes(value.state as State) ||
    !("expiresAt" in value) ||
    typeof value.expiresAt !== "string" ||
    !Number.isFinite(Date.parse(value.expiresAt))
  )
    throw new Error("Private sign-in status could not be confirmed.");
  return {
    state: value.state as State,
    expiresAt: value.expiresAt,
    ...("browserReady" in value && typeof value.browserReady === "boolean"
      ? { browserReady: value.browserReady }
      : {}),
  };
}
export function EnvironmentHumanSignIn({
  sessionId,
  onResumed,
  onStopped,
  retryMessage = "Run again to start a new session.",
}: {
  sessionId: string;
  onResumed(): void;
  onStopped?(): void;
  retryMessage?: string;
}) {
  const [session, setSession] = useState<Session | null>(null);
  const [browserOpen, setBrowserOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const generation = useRef(0),
    epoch = useRef(0),
    actionBusy = useRef(false),
    notified = useRef(false);
  const callback = useRef(onResumed);
  callback.current = onResumed;
  const stoppedCallback = useRef(onStopped);
  stoppedCallback.current = onStopped;
  const stopped = useRef(false);
  const notifyStopped = () => {
    if (!stopped.current) {
      stopped.current = true;
      stoppedCallback.current?.();
    }
  };
  const base = `/api/qa/interactive/${encodeURIComponent(sessionId)}`;
  const apply = (next: Session) => {
    const expired =
      ["waiting", "exporting"].includes(next.state) &&
      Date.parse(next.expiresAt) <= Date.now();
    const value = expired ? { ...next, state: "expired" as const } : next;
    setSession(value);
    if (value.state !== "waiting") setBrowserOpen(false);
    if (["resuming", "completed"].includes(value.state) && !notified.current) {
      notified.current = true;
      callback.current();
    }
    if (["cancelled", "expired", "failed"].includes(value.state))
      notifyStopped();
    return value;
  };
  const report = (failure: unknown) => {
    setBrowserOpen(false);
    if (failure instanceof AccessUnavailable) {
      setUnavailable(true);
      setError("Private sign-in is unavailable or your access has changed.");
      notifyStopped();
    } else {
      setError(
        "Could not confirm private sign-in status. Reconnect to check access again.",
      );
      captureUiError(new Error("Private sign-in status unavailable."), {
        area: "Settings",
        operation: "environment_human_sign_in",
      });
    }
  };
  useEffect(() => {
    const current = ++generation.current;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    setSession(null);
    setBrowserOpen(false);
    setBusy(false);
    setError("");
    setUnavailable(false);
    actionBusy.current = false;
    notified.current = false;
    stopped.current = false;
    epoch.current++;
    const poll = async () => {
      const version = epoch.current;
      let again = true;
      try {
        const next = await readSession(base, "GET", controller.signal);
        if (generation.current !== current || controller.signal.aborted) return;
        if (version === epoch.current) {
          const value = apply(next);
          again = ["waiting", "exporting", "resuming"].includes(value.state);
        }
      } catch (failure) {
        if (generation.current !== current || controller.signal.aborted) return;
        if (version === epoch.current) report(failure);
        if (failure instanceof AccessUnavailable) again = false;
      }
      if (again && generation.current === current && !controller.signal.aborted)
        timer = setTimeout(() => void poll(), 3000);
    };
    void poll();
    return () => {
      generation.current++;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
    // Each session owns its polling generation; callbacks are held in refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);
  useEffect(() => {
    if (!session || session.state !== "waiting") return;
    const remaining = Date.parse(session.expiresAt) - Date.now();
    if (remaining > 2_147_483_647) return;
    const timer = setTimeout(
      () => {
        setBrowserOpen(false);
        setSession((current) =>
          current?.state === "waiting"
            ? { ...current, state: "expired" }
            : current,
        );
      },
      Math.max(0, remaining),
    );
    return () => clearTimeout(timer);
  }, [session]);
  const act = async (action: "start" | "resume" | "cancel") => {
    if (actionBusy.current || unavailable) return;
    actionBusy.current = true;
    const current = generation.current;
    epoch.current++;
    setBrowserOpen(false);
    setBusy(true);
    setError("");
    try {
      const next = await readSession(`${base}/${action}`, "POST");
      if (generation.current !== current) return;
      const value = apply(next);
      if (action === "start" && value.state === "waiting") {
        if (value.browserReady === false)
          setError("The private browser is starting. Reconnect in a moment.");
        else setBrowserOpen(true);
      }
    } catch (failure) {
      if (generation.current !== current) return;
      // A lost POST response says nothing about whether the server accepted it.
      // Reconcile with GET, without reopening the browser or repeating the POST.
      try {
        const next = await readSession(base);
        if (generation.current !== current) return;
        apply(next);
        if (next.state === "waiting") report(failure);
      } catch (reconcileFailure) {
        if (generation.current === current) report(reconcileFailure);
      }
    } finally {
      if (generation.current === current) {
        epoch.current++;
        actionBusy.current = false;
        setBusy(false);
      }
    }
  };
  const status: Record<State, string> = {
    waiting: "Waiting for your private sign-in.",
    exporting: "Transferring application authentication…",
    resuming: "Testing is resuming.",
    completed: "Private sign-in is complete.",
    cancelled: "Private sign-in was cancelled.",
    expired: `Private sign-in expired. ${retryMessage}`,
    failed: `Private sign-in could not complete. ${retryMessage}`,
  };
  return (
    <Pane material="staged" className="min-w-0 space-y-3 p-3">
      <h3>Private sign-in</h3>
      <p>
        Capture and agent actions are paused while you sign in. Choose Continue
        testing when the application is signed in.
      </p>
      <p role="status" aria-live="polite">
        {unavailable
          ? ""
          : session
            ? status[session.state]
            : "Checking private sign-in status…"}
      </p>
      {error && <p role="alert">{error}</p>}
      {!unavailable && session?.state === "waiting" && (
        <>
          <p className="text-sm">
            Sign-in ends at{" "}
            <time dateTime={session.expiresAt}>
              {new Date(session.expiresAt).toLocaleTimeString()}
            </time>
            .
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => void act("start")}
            >
              {browserOpen || error
                ? "Reconnect private sign-in"
                : "Open private sign-in"}
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() => void act("resume")}
            >
              Continue testing
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() => void act("cancel")}
            >
              Cancel sign-in
            </Button>
          </div>
        </>
      )}
      {!unavailable && session?.state === "exporting" && (
        <Button
          type="button"
          variant="secondary"
          disabled={busy}
          onClick={() => void act("cancel")}
        >
          Cancel sign-in
        </Button>
      )}
      {browserOpen && !unavailable && session?.state === "waiting" && (
        <iframe
          title="Private application sign-in"
          className="block h-[min(65vh,640px)] min-h-72 w-full rounded border border-current/15"
          src={`${base}/browser/vnc.html?autoconnect=1&resize=remote&path=${base.slice(1)}/websockify`}
          referrerPolicy="no-referrer"
          onError={() => {
            setBrowserOpen(false);
            setError(
              "The private browser disconnected. Reconnect to continue signing in.",
            );
          }}
        />
      )}
    </Pane>
  );
}
